import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { FOCUSABLE } from "../../hooks/focusable";
import { MAIN_REGION_ID } from "./useRegionToggle";

/** How long to keep looking. The list behind a nav item is fetched, so it arrives late. */
const DEADLINE_MS = 3000;

/**
 * A list that marks its rows says so; only when none does do we fall back to table rows, which
 * would otherwise match a summary table that happens to come before the real list.
 */
const MARKED_ROWS = "[data-row-index]";
const TABLE_ROWS = "tbody tr";

/**
 * How long a request stays good for. Activating a menu item for the page you are already on
 * navigates nowhere, so the effect never runs to spend the request — and without an expiry it would
 * sit there until some unrelated later navigation, such as a step change inside the wizard, took it.
 */
const REQUEST_TTL_MS = 1000;

// Not 0: performance.now() counts from page load, so 0 would read as "a moment ago" for the
// first second of the app's life.
let requestedAt = Number.NEGATIVE_INFINITY;

/**
 * Says that the navigation just started came from the keyboard, so the page it lands on should take
 * focus rather than leaving it behind in the menu.
 */
export const requestContentFocus = (): void => {
  requestedAt = performance.now();
};

/**
 * The first row that can actually take focus — itself when it is focusable, otherwise the first
 * control inside it, since a table row is a plain <tr> and it is the link in a cell that can be
 * focused. Rows are tried in order: a leading row of headings or plain text is not a dead end.
 */
const firstRowTarget = (): HTMLElement | null => {
  const main = document.getElementById(MAIN_REGION_ID);
  if (!main) return null;
  const marked = main.querySelectorAll<HTMLElement>(MARKED_ROWS);
  const rows = marked.length > 0 ? marked : main.querySelectorAll<HTMLElement>(TABLE_ROWS);
  for (const row of rows) {
    if (row.matches(FOCUSABLE)) return row;
    const inside = row.querySelector<HTMLElement>(FOCUSABLE);
    if (inside) return inside;
  }
  return null;
};

/**
 * After a keyboard activation in the menu, moves focus to the first row of whatever the page lists.
 * Without it focus stays on the menu item and the reader has to tab past the rest of the menu to
 * reach what they just asked for.
 */
export const useFocusFirstRow = (): void => {
  const { pathname, search } = useLocation();
  const frame = useRef<number>();

  useEffect(() => {
    const requested = requestedAt;
    requestedAt = Number.NEGATIVE_INFINITY;
    if (performance.now() - requested > REQUEST_TTL_MS) return;

    const deadline = performance.now() + DEADLINE_MS;
    let cancelled = false;
    // The reader changed their mind and is driving somewhere else; stealing focus now would fight
    // them for a list they have stopped waiting on.
    const abort = () => {
      cancelled = true;
    };
    window.addEventListener("keydown", abort, true);
    window.addEventListener("pointerdown", abort, true);

    const attempt = () => {
      if (cancelled) return;
      const target = firstRowTarget();
      if (target) {
        target.focus();
        return;
      }
      if (performance.now() < deadline) frame.current = requestAnimationFrame(attempt);
    };
    frame.current = requestAnimationFrame(attempt);

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", abort, true);
      window.removeEventListener("pointerdown", abort, true);
      if (frame.current !== undefined) cancelAnimationFrame(frame.current);
    };
  }, [pathname, search]);
};
